import React from 'react';
import {useNavigate} from 'react-router';
import {Button, Chip, IconButton, Popover, Stack, Tooltip, Typography, Box, Divider, CircularProgress} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import {
    getReportStatusChipColor,
    getReportStatusChipLabel,
    isApprovedReportStatus,
} from '../../utils/reportStatusColor';
import {openEntityGroupChat} from '../../../../utils/openEntityGroupChat';
import {getReportEntityStats} from '../../../../utils/api/adminEntities';

// ── Mini stat row ─────────────────────────────────────────────────────────────

function StatRow({label, value, color}) {
    const colorMap = {
        default: 'text.primary',
        green:   'success.main',
        blue:    'info.main',
        amber:   'warning.main',
    };
    return (
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, py: 0.5}}>
            <Typography variant="caption" color="text.secondary" sx={{whiteSpace: 'nowrap'}}>{label}</Typography>
            <Typography variant="caption" fontWeight={700} color={colorMap[color] || 'text.primary'}
                        sx={{fontVariantNumeric: 'tabular-nums'}}>
                {value}
            </Typography>
        </Box>
    );
}

// ── Entity stats popover ──────────────────────────────────────────────────────

function EntityStatsPopover({reportId, anchorEl, onClose}) {
    const open = Boolean(anchorEl);
    const navigate = useNavigate();
    const [stats, setStats] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        if (!open || !reportId) return;
        setStats(null);
        setError(null);
        setLoading(true);
        getReportEntityStats(reportId)
            .then(setStats)
            .catch((e) => setError(e.message || 'Failed to load'))
            .finally(() => setLoading(false));
    }, [open, reportId]);

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
            transformOrigin={{vertical: 'top', horizontal: 'right'}}
            PaperProps={{
                sx: {width: 260, p: 2, borderRadius: 2, boxShadow: 4},
            }}
        >
            <Typography variant="subtitle2" fontWeight={700} mb={1}>
                Entity Statistics
            </Typography>
            <Divider sx={{mb: 1}}/>

            {loading && (
                <Box sx={{display: 'flex', justifyContent: 'center', py: 1}}>
                    <CircularProgress size={20}/>
                </Box>
            )}

            {error && (
                <Typography variant="caption" color="error">{error}</Typography>
            )}

            {stats && !loading && (
                <>
                    <StatRow label="Mentions" value={stats.totalMentions.toLocaleString()} color="default"/>
                    <Divider sx={{my: 0.5}}/>
                    <Typography variant="caption" color="text.disabled" sx={{display: 'block', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                        Wikidata (Q)
                    </Typography>
                    <StatRow label="Mentions" value={(stats.linkedToWikidata ?? 0).toLocaleString()} color="green"/>
                    <StatRow label="Unique QID" value={(stats.wikidataEntities ?? 0).toLocaleString()} color="green"/>
                    <Divider sx={{my: 0.5}}/>
                    <Typography variant="caption" color="text.disabled" sx={{display: 'block', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                        DID (D)
                    </Typography>
                    <StatRow label="Mentions" value={(stats.linkedToDid ?? 0).toLocaleString()} color="blue"/>
                    <StatRow label="Unique DID" value={(stats.didEntities ?? 0).toLocaleString()} color="blue"/>
                    <Divider sx={{my: 0.5}}/>
                    <Typography variant="caption" color="text.disabled" sx={{display: 'block', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                        Unlinked
                    </Typography>
                    <StatRow label="Mentions" value={(stats.unlinkedMentions ?? 0).toLocaleString()} color="amber"/>
                </>
            )}

            <Divider sx={{mt: 1.5, mb: 1}}/>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                <Typography variant="caption" color="text.disabled">
                    Admin only · live from DB
                </Typography>
                <Button
                    size="small"
                    variant="text"
                    onClick={() => { onClose(); navigate(`/products/${reportId}/mentions`); }}
                    sx={{fontSize: '11px', textTransform: 'none', py: 0, minWidth: 0, color: 'primary.main'}}
                >
                    View all →
                </Button>
            </Box>
        </Popover>
    );
}

/**
 * ReportHeaderActions - Action buttons for the report header
 *
 * @param {boolean} isViewer - Whether current user is a viewer
 * @param {boolean} isPreview - Whether in preview mode
 * @param {string} status - Current report status
 * @param {Object} validation - Validation object from report
 * @param {boolean} submittingReview - Whether submitting for review
 * @param {boolean} recalling - Whether recalling from review
 * @param {function} onViewToggle - Handler for view toggle
 * @param {function} onSubmitForReview - Handler for submit for review
 * @param {function} onRecall - Handler for recall
 * @param {function} onMenuOpen - Handler for opening actions menu
 * @param {boolean} isAdmin - Whether the current user is an admin (shows entity stats icon)
 */
export default function ReportHeaderActions({
                                                isViewer,
                                                isPreview,
                                                status,
                                                reportId,
                                                title,
                                                validation: _validation,
                                                submittingReview,
                                                recalling,
                                                onViewToggle,
                                                onSubmitForReview,
                                                onRecall,
                                                onMenuOpen,
                                                isAdmin,
                                            }) {
    const isAwaitingReview = status === 'Awaiting Review';
    const isApproved = isApprovedReportStatus(status);
    const statusChipLabel = getReportStatusChipLabel(status);
    const statusChipColor = getReportStatusChipColor(status);

    const [statsAnchor, setStatsAnchor] = React.useState(null);

    const statusChip = (
        <Chip
            label={statusChipLabel}
            size="small"
            color={statusChipColor}
            variant="filled"
            sx={{width: 'fit-content', textTransform: 'capitalize', fontWeight: 500}}
        />
    );

    const handleOpenTeamGroup = () => {
        if (!reportId) return;
        openEntityGroupChat({
            entityType: 'product',
            entityId: reportId,
            title: title || 'Product',
        });
    };

    return (
        <Stack direction={'row'} alignItems={'center'} spacing={1}>

            {isApproved && reportId && (
                <Tooltip title="Open product team chat" placement="bottom">
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PeopleAltOutlinedIcon fontSize="small"/>}
                        onClick={handleOpenTeamGroup}
                    >
                        Team
                    </Button>
                </Tooltip>
            )}

            {/* Submit for review, Recall button, or Approved label */}
            {!isViewer ? (
                <>
                    {isApproved ? (
                        statusChip
                    ) : isAwaitingReview ? (
                        <>
                            <Chip
                                label="Awaiting Review"
                                size="small"
                                color="warning"
                                variant="filled"
                                sx={{width: 'fit-content'}}
                            />
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={onRecall}
                                disabled={recalling}
                            >
                                {recalling ? 'Recalling…' : 'Recall'}
                            </Button>
                        </>
                    ) : (
                        <Button
                            size="small"
                            variant="contained"
                            onClick={onSubmitForReview}
                            disabled={submittingReview}
                        >
                            {submittingReview ? 'Submitting…' : 'Submit for review'}
                        </Button>
                    )}
                </>
            ) : (
                statusChip
            )}

            {/* Preview/Edit toggle */}
            {!isViewer && (
                <Button
                    size="small"
                    variant="outlined"
                    onClick={onViewToggle}
                >
                    {isPreview ? 'Edit' : 'Preview'}
                </Button>
            )}

            {/* Read-only access indicator for viewers */}
            {isViewer && (
                <Tooltip title="You have read-only access to this report" placement="bottom">
                    <Chip
                        icon={<VisibilityOutlinedIcon sx={{fontSize: '0.9rem !important'}}/>}
                        label="Read-only"
                        size="small"
                        variant="outlined"
                        sx={{
                            color: 'text.secondary',
                            borderColor: 'divider',
                            fontWeight: 400,
                            fontSize: '0.72rem',
                        }}
                    />
                </Tooltip>
            )}

            {/* Admin-only: entity stats icon */}
            {isAdmin && reportId && (
                <>
                    <Tooltip title="Entity statistics (admin)" placement="bottom">
                        <IconButton
                            size="small"
                            onClick={(e) => setStatsAnchor(e.currentTarget)}
                            aria-label="entity statistics"
                        >
                            <BarChartOutlinedIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                    <EntityStatsPopover
                        reportId={reportId}
                        anchorEl={statsAnchor}
                        onClose={() => setStatsAnchor(null)}
                    />
                </>
            )}

            {/* Three-dot menu button */}
            <IconButton
                aria-label="more"
                aria-haspopup="true"
                onClick={onMenuOpen}
            >
                <MoreVertIcon/>
            </IconButton>
        </Stack>
    );
}
